import { useId, useRef, useState } from 'react';
import { cn } from 'cn';
import { Icon } from './uploadUi';

/**
 * Where a khutbah file comes in.
 *
 * Drag-and-drop is an *addition* to a real file input, never a replacement:
 * dropping a file is a pointer gesture with no keyboard equivalent, so the
 * visible control is a button that opens the picker and the drop target wraps
 * it. The input itself stays in the DOM — hidden with `sr-only` rather than
 * `display:none`, so it keeps its accessible name and its label association.
 *
 * It deliberately does **not** set `accept` to a list of extensions. The
 * server reads the format from the file's own bytes, and an `accept` filter
 * built from extensions would hide a correctly-formatted file that happens to
 * be misnamed — which is precisely the case the byte sniffing exists to
 * handle. The limits are stated in words instead.
 */

export function DropZone({
  onPick,
  disabled = false,
  compact = false,
}: {
  onPick: (file: File) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draggingOver, setDraggingOver] = useState(false);
  const describedBy = useId();

  function take(files: FileList | null) {
    const file = files?.[0];
    if (file && !disabled) {
      onPick(file);
    }
  }

  return (
    <div
      onDragOver={(event) => {
        if (disabled) return;
        // Without preventDefault the browser navigates to the dropped file,
        // which loses the page and everything typed into it.
        event.preventDefault();
        setDraggingOver(true);
      }}
      onDragLeave={() => setDraggingOver(false)}
      onDrop={(event) => {
        if (disabled) return;
        event.preventDefault();
        setDraggingOver(false);
        take(event.dataTransfer.files);
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg px-4 text-center',
        'border border-dashed transition-colors',
        compact ? 'min-h-[132px] py-5' : 'min-h-[160px] py-6',
        draggingOver ? 'border-primary bg-accent' : 'border-border bg-background',
        disabled && 'opacity-60',
      )}
      style={{ transitionDuration: 'var(--duration-fast)' }}
    >
      <Icon name="upload" size={22} className="text-muted-foreground" />

      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        aria-describedby={describedBy}
        className={cn(
          'min-h-11 rounded-md px-3 font-semibold text-foreground',
          'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
          !disabled && 'hover:bg-muted',
        )}
      >
        {compact ? 'Tap to choose a file' : 'Drop the khutbah here or choose a file'}
      </button>

      <p id={describedBy} className="max-w-[420px] text-xs text-muted-foreground">
        PDF · Word (.doc, .docx) · plain text (.txt, UTF-8) · up to 25 MB, 40 pages.
        <br />
        The format is read from the file&rsquo;s own bytes, not its name — a renamed
        file is detected as what it is.
      </p>

      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        aria-label="Khutbah file"
        disabled={disabled}
        onChange={(event) => {
          take(event.target.files);
          // Clearing the value means picking the *same* file again still
          // fires change — otherwise a retry after a failure does nothing.
          event.target.value = '';
        }}
      />
    </div>
  );
}
